import {
  Skill,
  SkillOption,
  SkillParam,
  SkillValues,
  defaultPeriod,
  describeSkillRun,
  fetchSkillOptions,
  validateSkill
} from '@/lib/skills';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Package, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

const SEARCH_DELAY_MS = 250;

/** A searchable list of the customer's own values (items, accounts...). */
const Picker = ({
  skill,
  param,
  value,
  onChange
}: {
  skill: string;
  param: SkillParam;
  value?: { value: string; label: string };
  onChange: (v?: { value: string; label: string }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<SkillOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const res = await fetchSkillOptions(skill, param.name, q, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setOptions(res.options);
      setTimedOut(res.timedOut);
      setLoading(false);
    }, SEARCH_DELAY_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [open, q, skill, param.name]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="flex-1 min-w-0 justify-between font-normal h-10"
            dir="auto"
          >
            <span
              className={cn('truncate', !value && 'text-muted-foreground')}
              dir="auto"
            >
              {value
                ? value.label.trim()
                : `Search ${param.label.split(' (')[0].toLowerCase()}…`}
            </span>
            <ChevronsUpDown className="!size-4 opacity-50 flex-none" />
          </Button>
        </PopoverTrigger>
        {value && !param.required ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 flex-none"
            aria-label={`Clear ${param.label}`}
            onClick={() => onChange(undefined)}
          >
            <X className="!size-4" />
          </Button>
        ) : null}
      </div>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Type part of the name or the code"
            dir="auto"
          />
          <CommandList className="max-h-[300px]">
            {!loading && (
              <CommandEmpty className="py-5 px-3 text-sm text-muted-foreground text-center">
                {timedOut
                  ? 'The search took too long. Type more of the name.'
                  : q
                    ? 'Nothing matches.'
                    : 'Type to search.'}
              </CommandEmpty>
            )}
            {loading && (
              <div className="py-5 text-sm text-muted-foreground text-center">
                Searching…
              </div>
            )}
            {!loading && options.length > 0 && (
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => {
                      onChange({ value: o.value, label: o.label });
                      setOpen(false);
                    }}
                    className="cursor-pointer gap-2.5"
                  >
                    {param.type === 'item' && (
                      <span
                        className="flex items-center justify-center overflow-hidden flex-none"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          background: '#fff',
                          border: '1px solid var(--sig-border-light)'
                        }}
                      >
                        {o.image ? (
                          <img
                            src={o.image}
                            alt=""
                            loading="lazy"
                            className="max-h-full max-w-full object-contain"
                            style={{ padding: 2 }}
                            onError={(e) =>
                              ((e.target as HTMLImageElement).style.display =
                                'none')
                            }
                          />
                        ) : (
                          <Package className="!size-4 text-muted-foreground" />
                        )}
                      </span>
                    )}
                    <span className="flex flex-col min-w-0 flex-1">
                      <span className="truncate text-sm" dir="auto">
                        {o.label.trim()}
                      </span>
                      {o.hint || o.value !== o.label ? (
                        <span
                          className="truncate text-xs text-muted-foreground"
                          dir="auto"
                        >
                          {[o.value !== o.label ? o.value : null, o.hint]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      ) : null}
                    </span>
                    {value?.value === o.value && (
                      <Check className="!size-4 flex-none" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface Props {
  skill: Skill | null;
  /** Values to start from - a link in an answer opens the form filled in. */
  initial?: SkillValues;
  onClose: () => void;
  onRun: (text: string, skill: Skill, values: SkillValues) => void;
}

/**
 * One skill's form. The pickers return the ERP's own keys, the dates are
 * checked before anything is sent, and "Run" posts the form as a chat message
 * that the chat answers without the model.
 */
export const SkillDialog = ({ skill, initial, onClose, onRun }: Props) => {
  const [values, setValues] = useState<SkillValues>({});
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const firstOpen = useRef(true);

  useEffect(() => {
    if (!skill) return;
    const { from, to } = defaultPeriod();
    const known = new Set(skill.params.map((p) => p.name));
    const given = Object.fromEntries(
      Object.entries(initial || {}).filter(([k, v]) => known.has(k) && v?.value)
    );
    // A choice starts on its default, so the form is never sent half-chosen.
    const defaults = Object.fromEntries(
      skill.params
        .filter((p) => p.type === 'choice' && p.options?.length)
        .map((p) => {
          const o =
            p.options!.find((x) => x.value === p.default) || p.options![0];
          return [p.name, { value: o.value, label: o.label }];
        })
    );
    setValues({
      date_from: { value: from, label: from },
      date_to: { value: to, label: to },
      ...defaults,
      ...given
    });
    setError(null);
    setChoice(
      skill.one_of
        ? skill.one_of.find((n) => given[n]) || skill.one_of[0]
        : null
    );
    firstOpen.current = true;
  }, [skill, initial]);

  const shown = useMemo(
    () =>
      (skill?.params || []).filter(
        (p) => !skill?.one_of?.includes(p.name) || p.name === choice
      ),
    [skill, choice]
  );

  if (!skill) return null;

  const set = (name: string, v?: { value: string; label: string }) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setError(null);
  };

  const run = () => {
    // Only the alternative the user chose is sent.
    const sent: SkillValues = { ...values };
    for (const n of skill.one_of || []) if (n !== choice) delete sent[n];
    const problem = validateSkill(skill, sent);
    if (problem) {
      setError(problem);
      return;
    }
    onRun(describeSkillRun(skill, sent), skill, sent);
  };

  const pickers = shown.filter((p) => p.type !== 'date');
  const dates = shown.filter((p) => p.type === 'date');

  return (
    <Dialog open={!!skill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{skill.label}</DialogTitle>
          <DialogDescription>{skill.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          {skill.one_of && (
            <div
              className="grid gap-1 p-1 rounded-lg bg-muted"
              style={{
                gridTemplateColumns: `repeat(${skill.one_of.length}, 1fr)`
              }}
              role="tablist"
            >
              {skill.one_of.map((n) => {
                const p = skill.params.find((x) => x.name === n)!;
                return (
                  <button
                    key={n}
                    type="button"
                    role="tab"
                    aria-selected={choice === n}
                    onClick={() => {
                      setChoice(n);
                      setError(null);
                    }}
                    className={cn(
                      'h-8 rounded-md text-[13px] font-medium transition-colors',
                      choice === n
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}

          {pickers.map((p) => (
            <label key={p.name} className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">{p.label}</span>
              {p.type === 'choice' ? (
                <div className="flex flex-wrap gap-1.5" role="radiogroup">
                  {(p.options || []).map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={values[p.name]?.value === o.value}
                      onClick={() =>
                        set(p.name, { value: o.value, label: o.label })
                      }
                      className={cn(
                        'h-8 px-3 rounded-full border text-[13px] transition-colors',
                        values[p.name]?.value === o.value
                          ? 'bg-primary/10 border-primary/40 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              ) : p.type === 'text' || p.type === 'number' ? (
                <input
                  type={p.type === 'number' ? 'number' : 'text'}
                  inputMode={p.type === 'number' ? 'decimal' : undefined}
                  value={values[p.name]?.value || ''}
                  onChange={(e) =>
                    set(
                      p.name,
                      e.target.value.trim()
                        ? {
                            value: e.target.value.trim(),
                            label: e.target.value.trim()
                          }
                        : undefined
                    )
                  }
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                  dir="auto"
                />
              ) : (
                <Picker
                  skill={skill.id}
                  param={p}
                  value={values[p.name]}
                  onChange={(v) => set(p.name, v)}
                />
              )}
            </label>
          ))}

          {dates.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {dates.map((p) => (
                <label key={p.name} className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium">{p.label}</span>
                  <input
                    type="date"
                    value={values[p.name]?.value || ''}
                    max={defaultPeriod().to}
                    onChange={(e) =>
                      set(
                        p.name,
                        e.target.value
                          ? { value: e.target.value, label: e.target.value }
                          : undefined
                      )
                    }
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    dir="ltr"
                  />
                </label>
              ))}
            </div>
          )}

          {error && (
            <p
              className="text-[13px]"
              style={{ color: 'var(--sig-warn-icon)' }}
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={run}>
            Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillDialog;
